import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./HomePage.css";
import {
  FaEnvelopeOpen,
  FaRss,
  FaSearch,
  FaSearchengin,
  FaShieldAlt,
  FaArrowRight,
} from "react-icons/fa";
import { useModuleSettings } from "../context/ModuleSettingsContext";

// Each tool carries its own accent colour, which drives the icon chip, hover
// glow and CTA across both light and dark themes (see HomePage.css). Accents
// are chosen to read clearly with white CTA text.
const TOOL_CARDS = [
  {
    moduleKey: "phishing-ai",
    to: "phishing-ai",
    icon: <FaEnvelopeOpen />,
    title: "AI Phishing Analysis",
    desc: "Upload suspicious emails and get an AI-assisted verdict in seconds.",
    meta: "Optimized for .eml / .msg",
    accent: "#6366f1",
  },
  {
    moduleKey: "phishing",
    to: "phishing",
    icon: <FaEnvelopeOpen />,
    title: "Phishing Analysis",
    desc: "Upload suspicious emails and get an automated non-AI verdict in seconds.",
    meta: "Optimized for .eml / .msg",
    accent: "#2563eb",
  },
  {
    moduleKey: "threat-intel",
    to: "threat-intel",
    icon: <FaRss />,
    title: "Threat Intelligence",
    desc: "Stay current on phishing campaigns and security news curated from the top cyber outlets.",
    meta: "BleepingComputer, The Hacker News, Dark Reading & more",
    accent: "#0ea5e9",
  },
  {
    moduleKey: "ioc",
    to: "ioc",
    icon: <FaSearch />,
    title: "Unified IoC Validator",
    desc: "Validate IPs, domains, URLs, hashes, and emails across multiple OSINT providers in a single pass.",
    meta: "VirusTotal, AbuseIPDB & more",
    accent: "#059669",
  },
  {
    moduleKey: "query-generator",
    to: "query-generator",
    icon: <FaSearchengin />,
    title: "NL to KQL Generator",
    desc: "Generate queries for various SIEM and EDR platforms for quick investigations.",
    meta: "Defender, Sentinel, Splunk, Google SecOps, CrowdStrike",
    accent: "#7c3aed",
  },
  {
    moduleKey: "vulnerabilities",
    to: "vulnerabilities",
    icon: <FaShieldAlt />,
    title: "Vulnerability Center",
    desc: "Track and prioritize vulnerabilities across the asset inventory.",
    meta: "Asset inventory, severity scoring",
    accent: "#e11d48",
    beta: true,
  },
];

function getStoredUser() {
  const raw = localStorage.getItem("auth_user") || sessionStorage.getItem("auth_user");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function firstName(user) {
  const full = (user?.name || user?.username || "").trim();
  if (!full) return "";
  return full.split(/\s+/)[0];
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function HomePage() {
  const { isEnabled } = useModuleSettings();
  const visibleCards = TOOL_CARDS.filter((c) => isEnabled(c.moduleKey));
  const name = useMemo(() => firstName(getStoredUser()), []);
  const greet = useMemo(greeting, []);

  return (
    <div className="home-root">
      <div className="home-inner">
        <section className="home-hero">
          <span className="home-badge">AI-powered phishing investigations</span>
          <h1>
            {greet}
            {name ? `, ${name}` : ""}
          </h1>
          <p>
            Securely analyze emails and indicators of compromise from a unified,
            analyst-friendly workspace.
          </p>
        </section>

        <div className="home-section-head">
          <h2 className="home-section-title">Your workspace</h2>
          <span className="home-section-count">
            {visibleCards.length} {visibleCards.length === 1 ? "tool" : "tools"} available
          </span>
        </div>

        <section className="tools-grid-wrapper">
          <div className="tools-grid">
            {visibleCards.map((c, i) => (
              <Link
                key={c.moduleKey}
                to={c.to}
                className="tool-card"
                style={{ "--accent": c.accent, animationDelay: `${i * 55}ms` }}
              >
                <div className="tool-card-inner">
                  <div className="tool-card-head">
                    <span className="tool-icon" aria-hidden>
                      {c.icon}
                    </span>
                    {c.beta && <span className="tool-badge-beta">Beta</span>}
                  </div>
                  <h3 className="tool-title">{c.title}</h3>
                  <p className="tool-desc">{c.desc}</p>
                  <div className="tool-meta">{c.meta}</div>
                  <div className="tool-foot">
                    <span className="tool-cta">
                      Open
                      <FaArrowRight className="tool-cta-arrow" aria-hidden />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {visibleCards.length === 0 && (
              <div className="tools-empty">
                All modules are currently disabled. Ask an administrator to enable one in
                Settings → Modules.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
